import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import Navbar from './components/Navbar';
import Footer from './components/layout/Footer';
import SplashScreen from './components/SplashScreen';

// Pages
import LoginPage from './pages/auth/LoginPage';
import Dashboard from './pages/Dashboard';
import TeamsPage from './pages/TeamsPage';
import PlayersPage from './pages/PlayersPage';
import LeaguePage from './pages/league/LeaguePage';
import FixturesPage from './pages/FixturesPage';
import MatchPage from './pages/MatchPage';

// Team Pages
import CreateTeamPage from './pages/teams/CreateTeamPage';
import TeamDetailPage from './pages/teams/TeamDetailPage';
import EditTeamPage from './pages/teams/EditTeamPage';

// Player Pages
import CreatePlayerPage from './pages/players/CreatePlayerPage';
import PlayerDetailPage from './pages/players/PlayerDetailPage';
import EditPlayerPage from './pages/players/EditPlayerPage';

// League Pages
import CreateLeaguePage from './pages/league/CreateLeaguePage';
import EditLeaguePage from './pages/league/EditLeaguePage';
import TeamAssignmentPage from './pages/league/TeamAssignmentPage';
import FixtureGeneratorPage from './pages/league/FixtureGeneratorPage';
import LiveScoringPage from './pages/league/LiveScoringPage';
import PlayoffEnginePage from './pages/league/PlayoffEnginePage';
import LiveLeaderboardPage from './pages/league/LiveLeaderboardPage';
import PlayerStatsPage from './pages/league/PlayerStatsPage';

// Fixture Pages
import GenerateFixturesPage from './pages/fixtures/GenerateFixturesPage';

// Fan Pages
import FanDashboardPage from './pages/fan/FanDashboardPage';
import FanViewPage from './pages/fan/FanViewPage';

// Admin Pages
import UserManagementPage from './pages/admin/UserManagementPage';
import ResetDataPage from './pages/admin/ResetDataPage';

// Results Page
import ResultsPage from './pages/ResultsPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading">Loading...</div>
      </div>
    );
  }
  
  return currentUser ? children : <Navigate to="/login" />;
};

// Main App Component
const AppContent = () => {
  const [showSplash, setShowSplash] = useState(true);
  const { currentUser, isScoreKeeper } = useAuth();

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Navbar is now handled by the component itself

  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} />;
  }

  return (
    <Router>
      <div className="min-h-screen flex flex-col">
        {/* Only show navbar for authenticated users */}
        {currentUser && <Navbar />}
        
        <main className="flex-1">
          <AnimatePresence mode="wait">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Root redirect */}
              <Route 
                path="/" 
                element={
                  <Navigate to={
                    currentUser 
                      ? (isScoreKeeper() ? "/fixtures" : "/dashboard")
                      : "/fan"
                  } />
                } 
              />
              
              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/teams"
                element={
                  <ProtectedRoute>
                    <TeamsPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/teams/create"
                element={
                  <ProtectedRoute>
                    <CreateTeamPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/teams/:teamId"
                element={
                  <ProtectedRoute>
                    <TeamDetailPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/teams/:teamId/edit"
                element={
                  <ProtectedRoute>
                    <EditTeamPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/players"
                element={
                  <ProtectedRoute>
                    <PlayersPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/players/create"
                element={
                  <ProtectedRoute>
                    <CreatePlayerPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/players/:playerId"
                element={
                  <ProtectedRoute>
                    <PlayerDetailPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/players/:playerId/edit"
                element={
                  <ProtectedRoute>
                    <EditPlayerPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/league"
                element={
                  <ProtectedRoute>
                    <LeaguePage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/league/create"
                element={
                  <ProtectedRoute>
                    <CreateLeaguePage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/league/:leagueId/edit"
                element={
                  <ProtectedRoute>
                    <EditLeaguePage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/league/:leagueId/teams"
                element={
                  <ProtectedRoute>
                    <TeamAssignmentPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/league/:leagueId/fixtures"
                element={
                  <ProtectedRoute>
                    <FixtureGeneratorPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/league/:leagueId/playoffs"
                element={
                  <ProtectedRoute>
                    <PlayoffEnginePage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/league/:leagueId/leaderboard"
                element={
                  <ProtectedRoute>
                    <LiveLeaderboardPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/league/:leagueId/stats"
                element={
                  <ProtectedRoute>
                    <PlayerStatsPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/match/:matchId/scoring"
                element={
                  <ProtectedRoute>
                    <LiveScoringPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/fixtures"
                element={
                  <ProtectedRoute>
                    <FixturesPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/results"
                element={
                  <ProtectedRoute>
                    <ResultsPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Fan Routes - Public Access */}
              <Route
                path="/fan"
                element={<FanDashboardPage />}
              />
              
              <Route
                path="/fan/match/:matchId"
                element={<FanViewPage />}
              />
              
              {/* Admin Routes */}
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute>
                    <UserManagementPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/admin/reset"
                element={
                  <ProtectedRoute>
                    <ResetDataPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/fixtures/generate"
                element={
                  <ProtectedRoute>
                    <GenerateFixturesPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/match/:matchId"
                element={
                  <ProtectedRoute>
                    <MatchPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to={currentUser ? "/dashboard" : "/fan"} />} />
            </Routes>
          </AnimatePresence>
        </main>
        
        <Footer />
      </div>
    </Router>
  );
};

// App with Auth Provider
const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;